
import { IDrawableObject, ObjectInteraction, RenderSettings, RenderMode } from "../Object/ObjectManager";
import { Context } from "../Engine";
import { PropertyAnimation } from "@spi/Common/Animation/PropertyAnimation";
import { VertexBuffer } from "../Object/VertexBuffer";
import { Octree, IBoundingBoxObject, BoundingBox } from "@spi/Common/Util";
import { Camera } from "../View/Camera";

export class VisualOctree<T extends IBoundingBoxObject> implements IDrawableObject {
    order: number = 6000; // debug stuff
    animatedProperties: PropertyAnimation;
    interaction: ObjectInteraction;

    private buffer: VertexBuffer;
    private tree: Octree<T>;

    constructor(protected ctx: Context) {
        this.buffer = new VertexBuffer(ctx.gl.createBuffer(), ctx.gl.LINES, 0, 0);
    }

    setTree(tree: Octree<T>, includeTree: boolean, includeObjects: boolean) {
        let gl = this.ctx.gl;
        let geometry: number[] = [];

        let generateTreeGeo = (t: Octree<T>) => {
            if (includeTree) {
                this.buildBoundingBox(t.region, geometry);
            }
            if (includeObjects) {
                t.objects.forEach((obj) => {
                    if (obj.getBoundingBox) {
                        this.buildBoundingBox(obj.getBoundingBox(), geometry);
                    }
                });
            }

            if (t.branches) {

                for (let branch of t.branches) {
                    generateTreeGeo(branch);
                }

            }
        };

        generateTreeGeo(tree);


        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(geometry), gl.DYNAMIC_DRAW);

        this.buffer.itemSize = 3;
        this.buffer.itemCount = geometry.length / 3;
    }

    private buildBoundingBox(boundingBox: BoundingBox, geometry: number[]) {
        let min = boundingBox.min;
        let max = boundingBox.max;
        geometry.push(
            min[0], min[1], min[2], max[0], min[1], min[2],
            min[0], min[1], min[2], min[0], max[1], min[2],
            min[0], min[1], max[2], max[0], min[1], max[2],
            min[0], min[1], max[2], min[0], max[1], max[2],

            max[0], max[1], min[2], max[0], min[1], min[2],
            max[0], max[1], min[2], min[0], max[1], min[2],
            max[0], max[1], max[2], max[0], min[1], max[2],
            max[0], max[1], max[2], min[0], max[1], max[2],

            min[0], min[1], min[2], min[0], min[1], max[2],
            min[0], max[1], min[2], min[0], max[1], max[2],
            max[0], max[1], min[2], max[0], max[1], max[2],
            max[0], min[1], min[2], max[0], min[1], max[2]
        );
    };

    public render(ctx: Context, camera: Camera, settings: RenderSettings) {
        let gl = ctx.gl;

        let shaderProgram = ctx.engine.shaderManager.getTestGeometryShader();

        ctx.setShader(shaderProgram);

        gl.bindBuffer(gl.ARRAY_BUFFER, this.buffer.glVertexBuffer);
        gl.vertexAttribPointer(shaderProgram.attributes.aVertexPosition, 3, gl.FLOAT, false, Float32Array.BYTES_PER_ELEMENT * 3, 0);

        gl.uniformMatrix4fv(shaderProgram.uniforms.uProjectionMatrix, false, camera.projectionMatrix);
        gl.uniformMatrix4fv(shaderProgram.uniforms.uModelViewMatrix, false, camera.modelViewMatrix);

        gl.drawArrays(gl.LINES, 0, this.buffer.itemCount);
    }

}
